// Evidence blobs for queued cancellation reports. localStorage holds the report record but is
// far too small for photos, a voice note or a video, so the bytes live in IndexedDB keyed by
// the evidence item's local id. No dependency: a few lines over the raw API.
//
// When IndexedDB is unavailable (private mode on some browsers, jsdom) blobs fall back to an
// in-memory map: they survive for the session, and a record that outlives the page finds its
// blob missing — the receipt then asks the agent to re-attach that file.

const DB_NAME = 'ielect-pu-cancel';
const STORE = 'media';

const memory = new Map<string, Blob>();
let dbPromise: Promise<IDBDatabase | null> | null = null;

const openDb = (): Promise<IDBDatabase | null> => {
	if (dbPromise) return dbPromise;
	dbPromise = new Promise((resolve) => {
		try {
			if (typeof indexedDB === 'undefined') {
				resolve(null);
				return;
			}
			const req = indexedDB.open(DB_NAME, 1);
			req.onupgradeneeded = () => {
				if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
			};
			req.onsuccess = () => resolve(req.result);
			req.onerror = () => resolve(null);
			req.onblocked = () => resolve(null);
		} catch {
			resolve(null);
		}
	});
	return dbPromise;
};

const run = async <T>(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest): Promise<T | undefined> => {
	const db = await openDb();
	if (!db) return undefined;
	return new Promise((resolve) => {
		try {
			const req = op(db.transaction(STORE, mode).objectStore(STORE));
			req.onsuccess = () => resolve(req.result as T);
			req.onerror = () => resolve(undefined);
		} catch {
			resolve(undefined);
		}
	});
};

/** True when blobs will survive a reload. */
export const durableMediaAvailable = async (): Promise<boolean> => !!(await openDb());

export const putMedia = async (id: string, blob: Blob): Promise<void> => {
	memory.set(id, blob);
	await run('readwrite', (s) => s.put(blob, id));
};

export const getMedia = async (id: string): Promise<Blob | null> => {
	const hit = memory.get(id);
	if (hit) return hit;
	const stored = await run<Blob>('readonly', (s) => s.get(id));
	return stored instanceof Blob ? stored : null;
};

export const deleteMedia = async (id: string): Promise<void> => {
	memory.delete(id);
	await run('readwrite', (s) => s.delete(id));
};
